import React, { useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { CATEGORY_META, uploadResourceChunked } from './resourceLibraryApi';

export const ResourceUploadDialog = ({ open, onOpenChange, subject, yearLevel, category, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [progress, setProgress] = useState(null);
  const meta = CATEGORY_META[category] || {};

  const reset = () => { setFile(null); setTitle(''); setDescription(''); setProgress(null); };

  const pickFile = (f) => {
    setFile(f);
    if (f && !title) setTitle(`Year ${yearLevel} ${subject} ${meta.label || ''}`.trim());
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Choose a PDF file first');
    setProgress(0);
    try {
      const res = await uploadResourceChunked({ file, subject, yearLevel, category, title, description, onProgress: setProgress });
      toast.success(`${meta.label} uploaded`);
      onUploaded(res);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
      setProgress(null);
    }
  };

  const busy = progress !== null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent data-testid="resource-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload {meta.label}</DialogTitle>
          <DialogDescription>Year {yearLevel} {subject} · PDF or Word, up to 200 MB. Uploads in parts so large files are safe.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="rl-file">File</Label>
            <Input id="rl-file" type="file" accept=".pdf,.doc,.docx,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={busy}
              onChange={(e) => pickFile(e.target.files?.[0] || null)} data-testid="resource-upload-file" />
          </div>
          <div>
            <Label htmlFor="rl-title">Title</Label>
            <Input id="rl-title" value={title} onChange={(e) => setTitle(e.target.value)} required disabled={busy} data-testid="resource-upload-title" />
          </div>
          <div>
            <Label htmlFor="rl-desc">Short note for teachers (optional)</Label>
            <Textarea id="rl-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy}
              placeholder="e.g. Covers Chapters 1–8, Term 1 & 2" data-testid="resource-upload-description" />
          </div>
          {busy && (
            <div className="space-y-1" data-testid="resource-upload-progress">
              <Progress value={progress} />
              <p className="text-xs text-slate-500">{progress < 100 ? `Uploading… ${progress}%` : 'Finishing…'}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !file} data-testid="resource-upload-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Upload
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
